import { Injectable } from '@angular/core';
import { AuthenticationState, AuthInfo } from '../../authentication/authentication-state';
import { ActivityLogger } from '../../activities/activity-logger';
import { Settings } from '../../core/settings';
import { MdlDialogService } from '@angular-mdl/core';
import { DL_LINKS_TOKEN, DownloadDialogComponent } from './download-dialog.component';
import { first, map, skipWhile } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { DlLink, LinkByDate, LinkByDocument, LinkByFile } from '../../documents/models/dlLink';
import { SampleReportInfo } from '../../documents/models/sample-report-info';
import { DomSanitizer } from '@angular/platform-browser';

export type DocumentAction = 'print' | 'save' | 'documentFile' | 'documentLink' | 'categoryLink' | 'sample-report';

export const printDocumentAction: DocumentAction = 'print';
export const zipDocuments: DocumentAction = 'save';
export const downloadDocumentByLinkAction: DocumentAction = 'documentLink';
export const downloadCategoryDocumentsAction: DocumentAction = 'categoryLink';

@Injectable()
export class DocumentDownloader {

    constructor(
        private authenticationState: AuthenticationState,
        private settings: Settings,
        private activityLogger: ActivityLogger,
        private sanitizer: DomSanitizer,
        private dialogService: MdlDialogService) {
    }

    public async downloadDocumentsByUrl(action: DocumentAction, dlLinks: DlLink[]) {
        this.activityLogger.logDownloadActivity(dlLinks);
        return this.showDialog(action, dlLinks);
    }

    public getVideoUrl(dlLink: LinkByFile) {
        this.activityLogger.logDownloadActivity([dlLink]);
        return this.authenticationState.authInfo
            .pipe(
                skipWhile((x: AuthInfo) => !x.isAuthenticated),
                first(),
                map(x => {
                    const linkParams = dlLink.toQueryParamsObject();
                    const params = new HttpParams({
                        fromObject: {
                            ...linkParams,
                            'ESA-Authentication': x.accessToken.accessToken
                        }
                    });
                    return `${this.settings.apiUrl}/download/documentFile?${params.toString()}`;
                })
            ).toPromise();
    }

    public downloadCategoryDocuments(link: LinkByDate) {
        this.activityLogger.logDownloadActivity(link);
        return this.showDialog(downloadCategoryDocumentsAction, link);
    }

    public downloadDocumentLink(notificationData: LinkByDocument) {
        this.activityLogger.logDownloadActivity(notificationData);
        return this.showDialog(downloadDocumentByLinkAction, notificationData);
    }

    public async downloadDocumentByUrl(dlLink: LinkByFile) {
        this.activityLogger.logDownloadActivity(dlLink);
        return this.showDialog('documentFile', dlLink);
    }

    public async downloadSampleReportByUrl(dlLink: SampleReportInfo) {
        return this.showDialog('sample-report', dlLink);
    }

    private async showDialog(action: DocumentAction, params: DlLink | DlLink[] | SampleReportInfo): Promise<boolean> {

        const mdlDialogReference = await this.dialogService.showCustomDialog({
                component: DownloadDialogComponent,
                providers: [
                    {
                        provide: DL_LINKS_TOKEN,
                        useValue: { url: `${this.settings.apiUrl}/download/${action}`, params }
                    }
                ],
                isModal: true
            })
            .toPromise();

        return await mdlDialogReference.onHide().toPromise<boolean>();
    }
}